<template>
	<div class="achievements">
		<div class="achievements__title">
			{{
				`${translate("ui.achievements")} (${getUnclaimedAchievements.length + getClaimedAchievements.length}/${achievements.length})`
			}}
			<AppIconButton
				class="achievements__btn--visibility"
				:type="hideClaimed ? IconTypes.HIDE : IconTypes.SHOW"
				@click="changeAchievementsVisibility($event)"
				:size="28"
			/>
		</div>
		<HorizontalDecorDivider />
		<div class="achievements__container">
			<AchievementBox
				v-for="(achievement, index) in getUnclaimedAchievements"
				:key="index"
				:achievement="achievement"
				@click="claimReward(achievement)"
			/>
			<AchievementBox
				v-for="(achievement, index) in filteredAchievements"
				:key="index"
				:achievement="achievement"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AchievementBox from "@/components/Achievements/AchievementBox.vue";
import AppIconButton from "@/components/AppIconButton.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import { AchievementStates } from "@/game/entities/config/achievements/achievement_states";
import useAchievementsStore from "@/store/useAchievementsStore";
import useResourcesStore from "@/store/useResourcesStore";
import type { Achievement } from "@/types/achievements";
import { IconTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, onMounted } from "vue";

const resourcesStore = useResourcesStore();
const { increase } = resourcesStore;

const achievementsStore = useAchievementsStore();
const {
	achievements,
	getUnclaimedAchievements,
	getNoUnclaimedAchievements,
	getOpenAchievements,
	getClaimedAchievements,
	hideClaimed,
} = storeToRefs(achievementsStore);

const filteredAchievements = computed((): Achievement[] =>
	hideClaimed.value
		? getOpenAchievements.value
		: getNoUnclaimedAchievements.value,
);

const changeAchievementsVisibility = (event: string): void => {
	if (event === "hide") {
		achievementsStore.setHideClaimed(true);
	} else if (event === "show") {
		achievementsStore.setHideClaimed(false);
	}
};

onMounted((): void => {
	if (achievements.value.length > 0) {
		achievementsStore.setAchievementsState();
	}
});

const claimReward = (achievement: Achievement): void => {
	if (achievement.state !== AchievementStates.UNCLAIMED) return;

	achievementsStore.setAchievementState(
		achievement.id,
		AchievementStates.CLAIMED,
	);
	setTimeout(() => {
		if (achievement.reward) {
			increase(
				achievement.reward.reward_resource,
				achievement.reward?.amount,
				`achievement claimed: ${achievement.id}`,
			);
		}
	}, 500);
};
</script>

<style lang="scss" scoped>
.achievements {
	width: 100%;
	@include flex-col-start;
	position: relative;
	gap: 0.5rem;
	padding: 0.7rem;
	padding-top: 0;
	&__title {
		padding-top: 0.5rem;
		width: 100%;
		color: #f4d278;
		font-weight: bold;
		font-size: 1.25rem;
		line-height: 1;
		text-align: center;
		text-transform: uppercase;
		@include flex-row-center;
	}
	&__btn--visibility {
		position: absolute;
		right: 0.65rem;
	}
	&__container {
		@include flex-col-center;
		flex: 1 1 0;
		gap: 0.5rem;
		width: 100%;
	}
	:deep(.horizontal-decor-divider) {
		display: flex;
		justify-content: center;
		width: 100%;
	}
}
</style>
